/**
 * TenantContext — resolves the tenant slug from the URL path (/t/<slug>/...)
 * and exposes it to every child component. No data access is gated by this
 * context — the JWT/API key remains authoritative for that. This context
 * exists purely to:
 *
 *   • display the correct tenant name in the UI chrome
 *   • fail-safe 404 when someone lands on /t/nonsense
 *   • provide a base path prefix for SPA navigation
 *
 * Deliberately simple: one network call on mount, no retries, no cache.
 * If the resolver returns 404 we render a friendly "tenant not found"
 * page rather than bleed state into the authenticated app.
 */
import React, { createContext, useContext, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';
const TenantContext = createContext(null);

export function useTenant() {
  return useContext(TenantContext);
}

export function tenantBasePath(slug) {
  return slug ? `/t/${slug}` : '';
}

export default function TenantProvider({ children }) {
  const { tenantSlug } = useParams();
  const [tenant, setTenant] = useState(null);
  const [status, setStatus] = useState('loading'); // 'loading' | 'ready' | 'not_found' | 'error'

  useEffect(() => {
    let ignore = false;
    if (!tenantSlug) { setStatus('ready'); return; }
    (async () => {
      try {
        const r = await fetch(`${API_URL}/api/tenants/by-slug/${encodeURIComponent(tenantSlug)}`);
        if (ignore) return;
        if (r.status === 404) { setStatus('not_found'); return; }
        if (!r.ok) { setStatus('error'); return; }
        setTenant(await r.json());
        setStatus('ready');
      } catch {
        if (!ignore) setStatus('error');
      }
    })();
    return () => { ignore = true; };
  }, [tenantSlug]);

  if (status === 'loading') {
    return (
      <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-secondary)' }}
           data-testid="tenant-context-loading">
        Loading workspace…
      </div>
    );
  }

  if (status === 'not_found') {
    return (
      <div style={{ padding: 40, maxWidth: 520, margin: '60px auto', textAlign: 'center' }}
           data-testid="tenant-not-found">
        <div style={{ fontSize: 28, fontWeight: 700, marginBottom: 8 }}>Workspace not found</div>
        <div style={{ color: 'var(--text-secondary)', marginBottom: 24 }}>
          We couldn&apos;t find a workspace called <strong>{tenantSlug}</strong>.
          Check the spelling, or ask your workspace admin for the correct link.
        </div>
        <a href="/login" className="btn btn-primary btn-sm" data-testid="tenant-not-found-login-link">
          Go to sign in
        </a>
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div style={{ padding: 40, textAlign: 'center' }} data-testid="tenant-context-error">
        <div style={{ fontWeight: 600, marginBottom: 8 }}>Couldn&apos;t reach the workspace service</div>
        <div style={{ color: 'var(--text-secondary)', fontSize: 13 }}>Please refresh in a moment.</div>
      </div>
    );
  }

  return (
    <TenantContext.Provider value={{ tenant, slug: tenantSlug, basePath: tenantBasePath(tenantSlug) }}>
      {children}
    </TenantContext.Provider>
  );
}
